/**
 * ArchiveCategoryWarningModal Component
 *
 * Confirmation modal for archiving categories (any type).
 * Shows warning with usage counts if category is in use.
 * Lists affected entity names (first 5, then "and X more").
 */

import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

const MAX_DISPLAYED_NAMES = 5;

export interface UsageGroup {
  /** Section header (e.g., "Criteria using this category:") */
  label: string;
  /** Singular form for count text (e.g., "criterion") */
  singularLabel: string;
  /** Plural form for count text (e.g., "criteria") */
  pluralLabel: string;
  /** Names of entities using this category */
  names: string[];
}

export interface ArchiveCategoryWarningModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The category being archived (undefined when modal closed) */
  category: { id: string; name: string } | undefined;
  /** Usage groups describing which entities reference this category */
  usageGroups: UsageGroup[];
  /** Handler for confirming archive */
  onConfirm: () => void;
  /** Handler for canceling */
  onCancel: () => void;
}

export const ArchiveCategoryWarningModal: React.FC<ArchiveCategoryWarningModalProps> = ({
  isOpen,
  category,
  usageGroups,
  onConfirm,
  onCancel,
}) => {
  const totalUsage = useMemo(() => {
    return usageGroups.reduce((sum, group) => sum + group.names.length, 0);
  }, [usageGroups]);

  const hasUsage = totalUsage > 0;

  // Format usage text (e.g., "3 criteria and 2 confluences")
  const formatUsageText = (): string => {
    const parts: string[] = [];
    for (const group of usageGroups) {
      const count = group.names.length;
      if (count > 0) {
        parts.push(`${count} ${count === 1 ? group.singularLabel : group.pluralLabel}`);
      }
    }
    return parts.join(' and ');
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onCancel()}
      size="md"
      placement="center"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              {hasUsage && <AlertTriangle className="w-5 h-5 text-warning" />}
              <span>Archive Category</span>
            </ModalHeader>

            <ModalBody className="space-y-3">
              {!hasUsage && (
                <p className="text-text-secondary">
                  Are you sure you want to archive &quot;{category?.name}&quot;?
                </p>
              )}

              {hasUsage && (
                <>
                  <p className="text-text-secondary">
                    This category is used by <strong>{formatUsageText()}</strong>.
                    Archive anyway?
                  </p>

                  {usageGroups.map((group) => {
                    if (group.names.length === 0) return null;

                    const sortedNames = [...group.names].sort((a, b) =>
                      a.localeCompare(b, undefined, { sensitivity: 'base' })
                    );
                    const displayedNames = sortedNames.slice(0, MAX_DISPLAYED_NAMES);
                    const overflow = sortedNames.length - MAX_DISPLAYED_NAMES;

                    return (
                      <div key={group.label} className="space-y-1">
                        <p className="text-sm text-text-tertiary">{group.label}</p>
                        <ul className="list-disc list-inside text-sm text-text-secondary pl-2">
                          {displayedNames.map((name, index) => (
                            <li key={index}>{name}</li>
                          ))}
                          {overflow > 0 && (
                            <li className="text-text-tertiary">
                              and {overflow} more...
                            </li>
                          )}
                        </ul>
                      </div>
                    );
                  })}
                </>
              )}

              <p className="text-sm text-text-tertiary">
                Archived categories remain functional &mdash; existing references are preserved.
              </p>
            </ModalBody>

            <ModalFooter>
              <Button variant="light" onPress={onCancel}>
                Cancel
              </Button>
              <Button color="warning" onPress={onConfirm}>
                Archive
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
